import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты
import Modal from '../Modal/Modal';
import FormContainer from '../CustomElements/FormContainer';
import Input from '../CustomElements/Input';
import Checkbox from '../CustomElements/Checkbox';
import Selector from '../CustomElements/Selector';
import TimeSelector from '../CustomElements/TimeSelector';
import CustomSelect from '../CustomElements/CustomSelect';
import CustomButton from '../CustomElements/CustomButton';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
// Кастомные хуки
import { useButtonValidation } from '../../customHook/useButtonValidation';
import useFormDataChanged from '../../customHook/useFormDataChanged';
// Действия Redux
import { remove } from '../../redux/actions/loader.action';

/**
 * Компонент EntityFormModal отображает модальное окно с формой создания, редактирования сущности или удаления сущности.
 * Форма строится на основе переданной конфигурации и поддерживает динамическую обязательность полей,
 * отображение пользовательских селектов, а также автоматическую подгрузку опций из Redux store.
 */
function EntityFormModal({ entityKey, config, initialData = null, onClose, entityMode, deletingItem, t }) {
  const dispatch = useDispatch();
  //данные о процессе
  const loader = useSelector((state) => state.loader);
  // Флаг режима работы с сущностью: редактирование, добавление или удаление
  const isEditMode = entityMode === 'edit';
  const isAddMode = entityMode === 'add';
  const isDeleteMode = entityMode === 'delete';

  // Локальное состояние формы и успешного добавления/редактирования
  const [formData, setFormData] = useState({});

  // локальное состояние для ошибок формы
  const [formErrors, setFormErrors] = useState({});

  // Флаг, управляющий отображением страницы подтверждения после успешного действия (добавление, редактирование, удаление)
  const [showSuccess, setShowSuccess] = useState(false);

  // Флаг, управляющий отображением модального окна подтверждения перед редактированием
  const [confirmEdit, setConfirmEdit] = useState(false);

  // Флаг, управляющий отображением модального окна подтверждения перед добавлением
  const [confirmAdd, setConfirmAdd] = useState(false);

  // список полей, которые НЕ обязательны
  const [optionalFields, setOptionalFields] = useState([]);

  // Получение опций из Redux store
  const options = useSelector((state) => state.options);

  /**
   * Мемоизированный объект начальных значений формы для сравнения с formData.
   * Учитывает поля sourceField и valueNormalizer из initialData и исключает указанные в config.excludedFields.
   */
  const cleanInitialForComparison = useMemo(() => {
    // Получаем список исключённых полей, которые не участвуют в сравнении
    const excluded = config?.excludedFields || [];

    // Получаем список скрытых полей, которые не отображаются в форме, но участвуют в сравнении
    const hidden = config?.hiddenFields || [];

    // Объект, в который будут собраны очищенные и нормализованные значения
    const cleanInitial = {};

    // Обрабатываем поля, описанные в formFields
    config?.formFields?.forEach(({ name, sourceField, valueNormalizer }) => {
      // Пропускаем поле, если оно исключено
      if (!excluded.includes(name)) {
        // Определяем, откуда брать значение: либо sourceField, либо name
        const sourceKey = sourceField || name;

        // Получаем "сырое" значение из initialData
        const rawValue = initialData?.[sourceKey];

        // Если указан valueNormalizer, применяем его для преобразования значения
        const normalizedValue = typeof valueNormalizer === 'function' ? valueNormalizer(rawValue) : rawValue;

        // Записываем нормализованное значение (или пустую строку, если значение отсутствует)
        cleanInitial[name] = normalizedValue ?? '';
      }
    });

    // Добавляем скрытые поля, если они не были уже добавлены
    hidden.forEach((field) => {
      // Добавляем, только если:
      // - поле не исключено
      // - поле ещё не присутствует в cleanInitial
      if (!excluded.includes(field) && !(field in cleanInitial)) {
        // Просто копируем значение из initialData без нормализации
        cleanInitial[field] = initialData?.[field] ?? '';
      }
    });

    // Возвращаем сформированный объект для сравнения с formData
    return cleanInitial;
  }, [initialData, config?.formFields, config?.excludedFields, config?.hiddenFields]);

  // Проверка валидности формы (активность кнопки сохранения)
  const isButtonDisabled = useButtonValidation(formData, optionalFields);
  //проверяем были ли изменения в форме
  const isFormEdit = useFormDataChanged(cleanInitialForComparison || {}, formData, true);

  // Инициализация значений формы при первом рендере или при получении новых initialData
  useEffect(() => {
    // Создаём объект, который будет содержать финальные значения для useState(formData)
    const initial = {};

    // Проходимся по каждому полю, описанному в конфиге формы
    config?.formFields?.forEach(({ name, sourceField, valueNormalizer }) => {
      // Определяем, из какого поля initialData брать значение:
      // если указан sourceField — используем его, иначе берём по имени самого поля
      const sourceKey = sourceField || name;

      // Получаем "сырое" значение из initialData
      const rawValue = initialData?.[sourceKey];

      // Если указан valueNormalizer (в конфиге), применяем его
      // Это позволяет, например, превратить [{ id, name }] → [id]
      const normalizedValue = typeof valueNormalizer === 'function' ? valueNormalizer(rawValue) : rawValue;

      // Записываем нормализованное значение в объект initial
      // Если данных нет, по умолчанию ставим пустую строку (для input'ов и текстовых полей)
      initial[name] = normalizedValue ?? '';
    });

    // Обрабатываем "скрытые" служебные поля (например, id),
    // которые не участвуют в конфиге формы, но всё равно должны попасть в formData
    config?.hiddenFields?.forEach((field) => {
      if (initialData?.[field] !== undefined) {
        initial[field] = initialData[field];
      }
    });
    // Устанавливаем финальные данные в состояние формы
    setFormData(initial);
    // Зависимости: обновлять, если поменялись initialData или поля формы
  }, [initialData, config.formFields, config.hiddenFields]);

  /**
   * Вычисление необязательных полей:
   * 1. Если поле по конфигурации явно помечено как required — оно обязательное.
   * 2. Если поле имеет dynamicRequired — определяем обязательность через эту функцию.
   * 3. Все остальные поля считаются необязательными.
   * Возвращаем массив имён необязательных полей (используется для валидации и звёздочек в label).
   */
  const computeOptionalFields = (formData, configFields, options) => {
    return configFields
      .filter((field) => {
        // Пропускаем обязательные поля
        if (field?.required) return false;

        // Если у поля есть функция dynamicRequired, считаем поле обязательным, если она вернёт true
        if (typeof field?.dynamicRequired === 'function') {
          return !field?.dynamicRequired(formData, options); // возвращаем true только если поле НЕ обязательно
        }

        // Если нет required и dynamicRequired — поле необязательное
        return true;
      })
      .map((field) => field?.name); // Возвращаем список имён всех необязательных полей
  };

  /**
   * При изменении формы, конфигурации или опций, пересчитываем необязательные поля.
   * Особенно актуально для случаев, когда позиция влияет на обязательность других полей.
   * Ждём загрузки позиций прежде чем пересчитывать.
   */
  useEffect(() => {
    if (!config?.formFields?.length || !options?.positions) return; // Ждём, пока поля и позиции будут доступны
    const computed = computeOptionalFields(formData, config.formFields, options);
    setOptionalFields(computed);
  }, [formData, config.formFields, options]);

  /**
   * При монтировании компонента запускаем загрузку опций (если указана fetchAction для поля).
   * Это позволяет динамически подгружать списки селектов — например, список дорог, должностей и т.п.
   */
  useEffect(() => {
    config.formFields.forEach((field) => {
      if (typeof field?.fetchAction === 'function') {
        dispatch(field.fetchAction());
      }
    });
  }, [config.formFields, dispatch]);

  /**
   * Обработчик изменения значения поля формы.
   * Обновляет formData и сбрасывает ошибку, если она есть.
   *
   * @param {string} name - имя поля (ключ)
   * @param {*} value - новое значение поля
   */
  const handleChange = (name, value) => {
    // Обновляем состояние формы
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Если есть ошибка для этого поля — очищаем её
    setFormErrors((prev) => {
      if (prev[name]) {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      }
      return prev;
    });
  };

  /**
   * Обработчик потери фокуса поля.
   * Выполняет валидацию, если для поля задана функция validate в конфиге.
   *
   * @param {string} name - имя поля (ключ)
   */
  const handleBlur = (name) => {
    const fieldConfig = config?.formFields?.find((field) => field.name === name);

    // Проверяем, есть ли функция валидации
    if (typeof fieldConfig?.validate === 'function') {
      // Валидация: передаем значение и текст ошибки
      const error = fieldConfig.validate(formData[name], fieldConfig.errorText);
      setFormErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  /**
   * Валидирует все поля формы перед сабмитом.
   * Использует функцию validate из конфигурации поля, если она задана.
   * @returns {boolean} - true, если нет ошибок; false, если есть ошибки.
   */
  const validateAllFields = () => {
    const newErrors = {};

    // Проходим по каждому полю из конфигурации формы
    config?.formFields?.forEach((field) => {
      const { name, validate, errorText } = field;

      // Если у поля задана функция валидации — вызываем её
      if (typeof validate === 'function') {
        const error = validate(formData[name], errorText);
        if (error) {
          newErrors[name] = error; // Добавляем ошибку в объект ошибок
        }
      }
    });

    // Обновляем состояние ошибок формы
    setFormErrors(newErrors);

    // Возвращаем true, если ошибок нет
    return Object.keys(newErrors).length === 0;
  };

  // Отправка данных на сервер для добавления новой записи
  const submitAdd = async () => {
    // Вызываем add-экшен из конфигурации, если он определён
    if (typeof config?.actions?.add === 'function') {
      await dispatch(config.actions.add(formData));
    }
  };

  // Отправка данных на сервер для обновления существующей записи
  const submitEdit = async () => {
    // Вызываем edit-экшен из конфигурации, если он определён
    if (typeof config?.actions?.edit === 'function') {
      await dispatch(config.actions.edit(formData, formData?.id));
    }
  };

  // Отправка запроса на удаление записи
  const submitDelete = async () => {    
    if (typeof config?.actions?.delete === 'function') {
      // Удаление по ID
      await dispatch(config.actions.delete(formData?.id, deletingItem));
    }
  };

  // обработка клика на кнопку "Добавить" или "Редактировать"
  const handlePrimaryAction = (e) => {
    e.preventDefault();
    // Если есть ошибки — отменяем отправку
    if (!validateAllFields()) return;
    if (isEditMode) {
      setConfirmEdit(true);
      return;
    }
    if (isAddMode) {
      setConfirmAdd(true);
    }
  };

  /**
   * Добавляет или удаляет звёздочку у лейбла в зависимости от обязательности поля
   * - Если поле стало обязательным — добавляет *
   * - Если стало необязательным — удаляет *
   *
   * @param {string} name - имя поля
   * @param {string} baseLabel - базовый текст лейбла из конфига
   * @returns {string} - модифицированный лейбл
   */
  const getLabelWithAsterisk = (name, baseLabel) => {
    const isOptional = optionalFields?.includes(name);
    const hasAsterisk = baseLabel?.trim()?.endsWith('*');
    if (!isOptional && !hasAsterisk) return `${baseLabel} *`;
    if (isOptional && hasAsterisk) return baseLabel?.trim()?.slice(0, -1)?.trim();
    return baseLabel;
  };

  /**
   * Рендер одного поля формы по типу: text, checkbox, select, customSelect, time
   * Добавляет динамическую звёздочку у лейбла через getLabelWithAsterisk.
   */
  const renderField = useCallback(
    (field, index) => {
      const { name, type, label, placeholder, optionsSource, isMulti, customValue, ...rest } = field;
      const value = formData?.[name] || '';
      const optionsList = options?.[optionsSource] || [];
      const shownLabel = getLabelWithAsterisk(name, label);
      switch (type) {
        case 'text':
          return (
            <Input
              key={index}
              name={name}
              label={shownLabel}
              placeholder={placeholder}
              value={value}
              onChange={(e) => handleChange(name, e?.target?.value)}
              onBlur={() => handleBlur(name)}
              messageError={formErrors?.[name]}
              {...rest}
            />
          );

        case 'checkbox':
          return (
            <Checkbox
              key={index}
              name={name}
              label={label}
              checked={!!value}
              onChange={(val) => handleChange(name, val)}
              {...rest}
            />
          );

        case 'select':
          return (
            <Selector
              key={index}
              name={name}
              label={shownLabel}
              placeholder={placeholder}
              value={
                isMulti || customValue
                  ? optionsList?.filter((opt) => value?.includes?.(opt.value))
                  : optionsList?.find((opt) => opt.value === value) || null
              }
              isMulti={isMulti}
              options={optionsList}
              onChange={(selected) => {
                let value;

                if (isMulti) {
                  value = (selected || []).map((opt) => opt?.value);
                } else if (customValue) {
                  // если кастомное значение, оборачиваем в массив
                  value = selected ? [selected].map((opt) => opt?.value) : [];
                } else {
                  value = selected?.value ?? null;
                }

                handleChange(name, value);
              }}
              {...rest}
            />
          );

        case 'customSelect':
          return (
            <CustomSelect
              key={index}
              name={name}
              label={shownLabel}
              formData={formData}
              setFormData={setFormData}
              onChange={(selected) => handleChange(name, selected?.value || '')}
              optionsName={optionsSource}
              {...rest}
            />
          );

        case 'time':
          return (
            <TimeSelector
              key={index}
              name={name}
              label={shownLabel}
              value={value}
              onChange={(val) => handleChange(name, val)}
              placeholder={placeholder}
              minuteStep={10}
              {...rest}
            />
          );

        default:
          return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialData, formData, options, optionalFields, formErrors]
  );

  const modalTitle = useMemo(() => {
    // Извлекаем поле, которое используется для отображения имени сущности в заголовке
    // Если не указано явно в конфиге, по умолчанию используется поле 'name'
    const titleField = config?.titleField ?? 'name';

    /**
     * Вспомогательная функция для получения отображаемого значения сущности.
     * - Если `titleField` — функция, вызываем её с объектом данных.
     * - Если `titleField` — строка, пытаемся взять соответствующее свойство из объекта.
     * - Если данных нет, возвращаем пустую строку.
     *
     * @param {Object} data - Объект сущности (например, initialData или deletingItem)
     * @returns {string} - Отображаемое имя сущности
     */
    const getDisplayValue = (data) => {
      if (!data) return '';
      return typeof titleField === 'function' ? titleField(data) : data?.[titleField] ?? '';
    };

    // Выбираем соответствующий заголовок в зависимости от режима:
    // - В режиме удаления: передаём имя в функцию titleDelete
    // - В режиме редактирования: передаём имя в titleEdit
    // - В режиме добавления: возвращаем статичный заголовок titleAdd
    if (isDeleteMode) return config?.titleDelete?.(getDisplayValue(deletingItem));
    if (isEditMode) return config?.titleEdit?.(getDisplayValue(initialData));
    if (isAddMode) return config?.titleAdd;

    // Если режим не определён — возвращаем пустую строку
    return '';
  }, [isDeleteMode, isEditMode, isAddMode, config, deletingItem, initialData]);

  /**
   * Возвращает текст успешного действия (add/edit/delete), подставляя имя по полю `resultTitleField`.
   */
  const getSuccessText = () => {
    const titleGetter = config?.resultTitleField ?? 'name'; // по умолчанию — 'name'

    const getValue = (data) => {
      if (!data) return '';
      return typeof titleGetter === 'function' ? titleGetter(data) : data?.[titleGetter] ?? '';
    };

    if (isDeleteMode) {
      return config?.titleDeleteResult?.(getValue(deletingItem));
    }

    if (isEditMode) {
      return config?.titleEditResult?.(getValue(formData));
    }

    if (isAddMode) {
      return config?.titleAddResult?.(getValue(formData));
    }

    return '';
  };

  useEffect(() => {
    // Следим за окончанием запроса и успешным статусом
    if (!loader?.isLoading && loader?.isSuccess) {
      setShowSuccess(true); // Показываем страницу подтверждения при успехе
    }

    // При размонтировании компонента сбрасываем статус загрузки
    return () => {
      dispatch(remove());
    };
  }, [loader?.isLoading, loader?.isSuccess, dispatch]);

  //проверяем можно ли активировать кнопку
  const hasErrors = Object.values(formErrors).some(Boolean);
  const isSubmitDisabled = isEditMode ? isButtonDisabled || !isFormEdit || hasErrors : isButtonDisabled || hasErrors;
  return (
    <Modal isOpen onClose={onClose} containerSelector=".adding-data-block__table">
      <section className="data-modal">
        <h3 className="custom-title_text ">{modalTitle}</h3>

        {/* Успешное добавление/редактирование */}
        {showSuccess && (
          <ConfirmationPage confirmText={getSuccessText()} onConfirm={onClose} addNew={t?.returnToListText} />
        )}

        {/* Подтверждение удаления */}
        {isDeleteMode && !showSuccess && (
          <ConfirmationPage
            confirmText={`${config.confirmDeleteText?.(deletingItem?.name || deletingItem?.email|| deletingItem?.fullName )} ?`}
            onConfirm={submitDelete}
            onCancel={onClose}
            confirmButtonText={config.confirmDeleteButton}
            cancelButtonText={config.cancelDeleteButton}
          />
        )}
        {/* Подтверждение редактирования или добавления */}
        {(isEditMode || isAddMode) && !showSuccess && (confirmEdit || confirmAdd) && (
          <ConfirmationPage
            confirmText={isEditMode ? config.confirmEditText : config.confirmAddText}
            onConfirm={isEditMode ? submitEdit : submitAdd}
            onCancel={() => {
              isEditMode ? setConfirmEdit(false) : setConfirmAdd(false);
            }}
            confirmButtonText={isEditMode ? config.editButtonText : config.addButtonText}
            cancelButtonText={config.cancelButtonText}
          />
        )}

        {/* Форма добавления/редактирования */}
        {!isDeleteMode && !showSuccess && !confirmEdit && !confirmAdd && (
          <FormContainer columns={config.formFields.length < 2 ? 1 : 2}>
            {/* Рендер всех полей формы */}
            {config.formFields.map(renderField)}

            {/* Кнопка отправки */}
            <CustomButton
              width="var(--sadr-font-size-420)"
              height="var(--sadr-font-size-60)"
              margin="0 0 var(--sadr-font-size-20) 0"
              text={isEditMode ? config?.editButtonText : config?.saveButtonText}
              onClick={handlePrimaryAction}
              disabled={isSubmitDisabled}
              isButton
            />
          </FormContainer>
        )}
      </section>
    </Modal>
  );
}

export default EntityFormModal;
